/**
 * sentry-error.ts — SentryError discriminated union
 *
 * Purpose:    Single typed error type for all ɳSentry plugin dashboard API calls.
 *             Each variant maps to a distinct user-facing message in AsyncScreen.
 * Inputs:     HTTP response status codes, network failures, license checks
 * Outputs:    SentryError variant with message string
 * Constraints: No external deps; must match the 6 variants defined in the ticket spec
 * SOT:        T-P3-E5-W2-S4-T01
 */

export type SentryErrorType =
  | 'plugin_offline'
  | 'auth'
  | 'rate_limit'
  | 'bundle_required'
  | 'config_error'
  | 'network'

export interface SentryError {
  type: SentryErrorType
  /** Human-readable message shown in error states */
  message: string
  /** Optional retry-after seconds for rate_limit */
  retryAfterSeconds?: number
  /** HTTP status of the failing response (absent for network/exception errors) */
  status?: number
}

/**
 * True when the failure looks like a gateway route that has not shipped yet
 * (404/405/501). Pages for features the gateway is still rolling out use this
 * to render a "coming online" panel instead of a scary error card.
 */
export function isComingOnline(e: SentryError | undefined): boolean {
  return e?.status === 404 || e?.status === 405 || e?.status === 501
}

const ERROR_MESSAGES: Record<SentryErrorType, string> = {
  plugin_offline: 'Plugin offline — check nself status',
  auth: 'Authentication required — please log in',
  rate_limit: 'Too many requests — please wait before retrying',
  bundle_required: 'ɳSentry bundle required — upgrade to access this dashboard',
  config_error: 'Plugin configuration error — run nself doctor to diagnose',
  network: 'Network error — check your connection and try again',
}

/**
 * Build a SentryError from an HTTP Response.
 * Maps common status codes to typed variants.
 */
export function errorFromResponse(
  res: Response,
  retryAfterSeconds?: number,
): SentryError {
  if (res.status === 401 || res.status === 403) {
    // 403 specifically for bundle gate
    const type: SentryErrorType = res.status === 403 ? 'bundle_required' : 'auth'
    return { type, message: ERROR_MESSAGES[type], status: res.status }
  }
  if (res.status === 429) {
    return {
      type: 'rate_limit',
      message: ERROR_MESSAGES.rate_limit,
      retryAfterSeconds,
      status: res.status,
    }
  }
  if (res.status === 503 || res.status === 0) {
    return { type: 'plugin_offline', message: ERROR_MESSAGES.plugin_offline, status: res.status }
  }
  return { type: 'config_error', message: ERROR_MESSAGES.config_error, status: res.status }
}

/**
 * Build a SentryError from a caught exception (fetch failed = network error,
 * AbortError = plugin_offline).
 */
export function errorFromException(e: unknown): SentryError {
  if (e instanceof DOMException && e.name === 'AbortError') {
    return { type: 'plugin_offline', message: ERROR_MESSAGES.plugin_offline }
  }
  if (!navigator.onLine) {
    return { type: 'network', message: ERROR_MESSAGES.network }
  }
  return { type: 'plugin_offline', message: ERROR_MESSAGES.plugin_offline }
}

export { ERROR_MESSAGES }
